// Pack 4 clues on the edge between two cells: kropki dots (difference white, ratio black, with
// the value inside when it isn't the usual one), XV and other edge sums, greater-than signs.
// Negative entries ("all dots given") draw nothing; ofType leaves them out.
import type { ConstraintEntry, PuzzleDoc } from '@zinc/shared';
import { chevron } from './Cells';
import { centre, num, ofType, rcList, styleOf, text } from './geom';
import { EDGE_SUM, ENDS, FONT, INK, KROPKI, SIGN, WHITE } from './style';

function ends(e: ConstraintEntry) {
  const cells = rcList(e);
  if (cells.length < 2) return null;
  const a = centre(cells[0]!);
  const b = centre(cells[1]!);
  return { a, b, mx: (a[0] + b[0]) / 2, my: (a[1] + b[1]) / 2 };
}

function Kropki({ e }: { e: ConstraintEntry }) {
  const at = ends(e);
  if (!at) return null;
  const black = e.type === 'ratio';
  // A grey dot that is white or black (kropki-either): filled with the line grey.
  const grey = e.type === 'kropki-either';
  const usual = black ? 2 : 1;
  const value = num(e, 'value') ?? usual;
  const shown = text(e, 'label') ?? (value === usual ? null : String(value));
  const { color } = styleOf(e, { color: INK, width: KROPKI.strokeWidth });
  const r = shown ? KROPKI.valueRadius : KROPKI.radius;
  return (
    <g data-constraint={e.type}>
      <circle
        cx={at.mx}
        cy={at.my}
        r={r}
        fill={grey ? ENDS.color : black ? color : WHITE}
        stroke={grey ? ENDS.color : color}
        strokeWidth={black || grey ? 0 : KROPKI.strokeWidth}
      />
      {shown && (
        <text
          x={at.mx}
          y={at.my + 0.5}
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily={FONT}
          fontSize={KROPKI.valueSize}
          fontWeight={700}
          fill={black ? WHITE : color}
        >
          {shown}
        </text>
      )}
    </g>
  );
}

function EdgeSum({ e }: { e: ConstraintEntry }) {
  const at = ends(e);
  if (!at) return null;
  const value = num(e, 'value');
  const shown =
    text(e, 'label') ??
    (value === 10 ? 'X' : value === 5 ? 'V' : value === null ? '' : String(value));
  if (!shown) return null;
  const { color } = styleOf(e, { color: INK, width: 1 });
  return (
    <text
      data-constraint="edge-sum"
      x={at.mx}
      y={at.my + 0.5}
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily={FONT}
      fontSize={EDGE_SUM.size}
      fontWeight={EDGE_SUM.weight}
      fill={color}
      stroke={WHITE}
      strokeWidth={EDGE_SUM.halo}
      strokeLinejoin="round"
      style={{ paintOrder: 'stroke' }}
    >
      {shown}
    </text>
  );
}

/** A chevron on the edge, its point towards the smaller cell (`cells[1]`). */
function GreaterThan({ e }: { e: ConstraintEntry }) {
  const at = ends(e);
  if (!at) return null;
  const dx = at.b[0] - at.a[0];
  const dy = at.b[1] - at.a[1];
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const { color, width } = styleOf(e, SIGN);
  const tip = SIGN.arm * 0.4;
  const d = chevron(at.mx + ux * tip, at.my + uy * tip, ux, uy, SIGN.arm);
  // A white halo so the sign reads on top of a box border.
  return (
    <g data-constraint="greater-than" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} stroke={WHITE} strokeWidth={width + SIGN.halo} />
      <path d={d} stroke={color} strokeWidth={width} />
    </g>
  );
}

export function Edges({ doc }: { doc: PuzzleDoc }) {
  return (
    <g>
      {ofType(doc, 'greater-than').map((e, i) => (
        <GreaterThan key={`g${i}`} e={e} />
      ))}
      {ofType(doc, 'difference', 'ratio', 'kropki-either').map((e, i) => (
        <Kropki key={`k${i}`} e={e} />
      ))}
      {ofType(doc, 'edge-sum').map((e, i) => (
        <EdgeSum key={`s${i}`} e={e} />
      ))}
    </g>
  );
}
